import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Traps focus inside the referenced element while active.
 * Also returns focus to the trigger element on unmount.
 *
 * Usage:
 *   const modalRef = useFocusTrap(isOpen);
 *   <div ref={modalRef} role="dialog" ...>
 */
const useFocusTrap = (isActive = true) => {
  const ref = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    if (!isActive) return;

    // Remember what had focus before the modal opened
    triggerRef.current = document.activeElement;

    const el = ref.current;
    if (!el) return;

    // Move focus into the modal
    const focusable = el.querySelectorAll(FOCUSABLE_SELECTORS);
    if (focusable.length) focusable[0].focus();

    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      const focusableList = Array.from(el.querySelectorAll(FOCUSABLE_SELECTORS));
      if (!focusableList.length) return;

      const first = focusableList[0];
      const last = focusableList[focusableList.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    el.addEventListener('keydown', handleKeyDown);
    return () => {
      el.removeEventListener('keydown', handleKeyDown);
      // Return focus to the element that opened the modal
      if (triggerRef.current && typeof triggerRef.current.focus === 'function') {
        triggerRef.current.focus();
      }
    };
  }, [isActive]);

  return ref;
};

export default useFocusTrap;
